import * as React from "react";
import { VariantProps, cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";

const textVariants = cva("text-foreground", {
  variants: {
    variant: {
      "display-lg":
        "font-pretendard text-[57px] font-normal font-bold leading-[64px] tracking-[-0.15px]",
      "display-md":
        "font-pretendard text-[45px] font-normal font-semibold leading-[52px]",
      "display-sm":
        "font-pretendard text-[36px] font-normal font-semibold leading-[44px]",
      "headline-lg":
        "font-pretendard text-[32px] font-normal font-semibold leading-[40px]",
      "headline-md":
        "font-pretendard text-[28px] font-normal font-bold leading-[36px]",
      "headline-sm":
        "font-pretendard text-[24px] font-normal font-bold leading-[32px]",
      "title-xxl":
        "font-pretendard text-[22px] font-normal font-extrabold leading-[28px]",
      "title-xl":
        "font-pretendard text-[20px] font-normal font-bold leading-[27px]",
      "title-lg":
        "font-pretendard text-[18px] font-normal font-semibold leading-[26px] tracking-[0.15px]",
      "title-md":
        "font-pretendard text-[16px] font-normal font-semibold leading-[23px] tracking-[0.15px]",
      "title-sm":
        "font-pretendard text-[14px] font-normal font-semibold leading-[20px] tracking-[0.1px]",
      "label-lg":
        "font-pretendard text-[14px] font-normal font-semibold leading-[20px]",
      "label-bold":
        "font-pretendard text-[13px] font-normal font-medium leading-[20px] tracking-[0.15px]",
      "label-md":
        "font-pretendard text-[12px] font-normal font-medium leading-[16px] tracking-[0.5px]",
      "label-sm":
        "font-pretendard text-[11px] font-normal font-medium leading-[16px] tracking-[0.5px]",
      "body-lg":
        "font-pretendard text-[16px] font-normal font-[400] leading-[24px] tracking-[0.5px]",
      "body-md":
        "font-pretendard text-[14px] font-normal font-semibold leading-[23px] tracking-[0.25px]",
      "body-regular":
        "font-pretendard text-[14px] font-normal font-[600] leading-[23px] tracking-[0.25px]",
      "body-light":
        "font-pretendard text-[14px] font-normal font-[400] leading-[23px] tracking-[0.25px]",
      "body-sm":
        "font-pretendard text-[12px] font-normal font-light leading-[16px]",
    },
  },
  defaultVariants: {
    variant: "body-md",
  },
});

type VariantPropType = VariantProps<typeof textVariants>;

export const variantElementMap: Record<
  NonNullable<VariantPropType["variant"]>,
  string
> = {
  "display-lg": "span",
  "display-md": "span",
  "display-sm": "span",
  "headline-lg": "span",
  "headline-md": "span",
  "headline-sm": "span",
  "title-xxl": "span",
  "title-xl": "span",
  "title-lg": "span",
  "title-md": "span",
  "title-sm": "span",
  "label-lg": "span",
  "label-bold": "span",
  "label-md": "span",
  "label-sm": "span",
  "body-lg": "span",
  "body-md": "span",
  "body-regular": "span",
  "body-light": "span",
  "body-sm": "span",
};

type TextElement = "h1" | "h2" | "h3" | "h4" | "p" | "small" | "div";

export interface TextProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof textVariants> {
  asChild?: boolean;
  as?: TextElement;
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ className, variant, as, asChild, ...props }, ref) => {
    const Comp = asChild
      ? Slot
      : as ?? (variant ? variantElementMap[variant] : undefined) ?? "div";
    return (
      <Comp
        className={cn(textVariants({ variant, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Text.displayName = "Text";

export { Text, textVariants };
